import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid lg:grid-cols-2 bg-background">
      <button
        onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/"))}
        data-testid="admin-back-button"
        aria-label="Torna alla pagina precedente"
        className="fixed top-5 left-5 lg:left-auto lg:right-5 z-50 group inline-flex items-center gap-2 bg-foreground text-background pl-3 pr-4 py-3 shadow-lg hover:bg-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
        <span className="font-mono text-xs uppercase tracking-widest">Indietro</span>
      </button>
      <div className="hidden lg:block bg-foreground relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative h-full flex flex-col justify-between p-14">
          <div className="flex items-center gap-3">
            <img
              src="/images/ghs-logo-icon-white.png?v=2"
              alt=""
              width="636"
              height="204"
              data-testid="admin-login-icon"
              className="h-14 w-auto"
              loading="eager"
            />
            <span className="w-3 h-3 bg-primary block" />
            <span className="font-head font-black text-lg tracking-tightest text-background" data-testid="admin-login-brand">GREENHOUSE SOLUTIONS<span className="text-primary">.</span> ADMIN</span>
          </div>
          <h1 className="font-head font-black text-6xl tracking-tightest text-background leading-[0.9]">
            Area<br /><span className="text-primary">Riservata</span>
          </h1>
          <p className="font-mono text-xs uppercase tracking-widest text-background/50">Gestione richieste & progetti</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-16">
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">// Accedi</p>
          <h2 className="font-head font-black text-4xl tracking-tightest mb-10">Login Amministratore</h2>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">Email</Label>
              <Input id="email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none h-12" placeholder="Inserisci la tua email" autoComplete="off" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest">Password</Label>
              <Input id="password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none h-12" placeholder="••••••••" />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-foreground transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Accedi <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
